import { defineStore, getActivePinia } from 'pinia';
import axios from 'axios';
import { router } from '../../router.js';
import Fingerprint2 from 'fingerprintjs2';
import config from '../../config/config.js';

const { baseURL } = config;

const savedUser = JSON.parse(localStorage.getItem('user'));

export const useAuthStore = defineStore('authentication', {
  state: () => ({
    status: savedUser ? { loggedIn: true } : {},
    user: savedUser || null
  }),
  actions: {
    login({ username, password }) {
      this.status = { loggingIn: true };
      this.user = { username };
      Fingerprint2.getPromise({ excludeAdBlock: true })
        .then(components => {
          const murmur = Fingerprint2.x64hash128(components.map(c => c.value).join(''), 31);
          axios.post(`${baseURL}/auth/login`, { login: username, password, murmur })
            .then(response => {
              if (response.data) {
                this.status = { loggedIn: true };
                this.user = response.data;
                localStorage.setItem('user', JSON.stringify(response.data));
                if (response.data.pending) {
                  router.push('/my-requests').catch(e => e);
                } else {
                  const { useUsersStore } = require('./users.module');
                  useUsersStore().fetchUserConfig();
                  router.push('/').then(() => {
                    const { useAlertStore } = require('./alert.module');
                    useAlertStore().success('Добро пожаловать, ' + response.data.name + '!');
                  }).catch(e => e);
                }
              }
            })
            .catch(error => {
              const { useAlertStore } = require('./alert.module');
              if (error.response?.status === 401) {
                useAlertStore().error('Не верный логин или пароль!');
              } else {
                // 403 — учётная запись заблокирована: сервер объясняет сам
                useAlertStore().error(error.response?.data?.message || 'Не удалось войти');
              }
              this.status = {};
              this.user = null;
            });
        });
    },
    register({ name, username, password, partnerId }) {
      this.status = { registering: true };
      axios.post(`${baseURL}/auth/register`, { name, login: username, password, partnerId })
        .then(() => {
          const { useAlertStore } = require('./alert.module');
          useAlertStore().success('Регистрация прошла успешно! Войдите в систему.');
          router.push('/login').catch(e => e);
        })
        .catch(error => {
          const { useAlertStore } = require('./alert.module');
          const message = error.response?.data?.message || 'Ошибка регистрации';
          useAlertStore().error(message);
        })
        .finally(() => {
          this.status = {};
        });
    },
    async updateProfile({ name, username }) {
      try {
        const response = await axios.post(`${baseURL}/auth/profile`, { name, username }, { withCredentials: true });
        const updatedUser = { ...this.user, name: response.data.name || name, username: response.data.username || username };
        this.user = updatedUser;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        const { useAlertStore } = require('./alert.module');
        useAlertStore().success('Профиль обновлён');
      } catch (error) {
        const { useAlertStore } = require('./alert.module');
        useAlertStore().error(error.response?.data?.message || 'Ошибка обновления профиля');
      }
    },
    async changePassword({ currentPassword, newPassword }) {
      try {
        await axios.post(`${baseURL}/auth/change-password`, { currentPassword, newPassword }, { withCredentials: true });
        const { useAlertStore } = require('./alert.module');
        useAlertStore().success('Пароль успешно изменён');
        return true;
      } catch (error) {
        const { useAlertStore } = require('./alert.module');
        useAlertStore().error(error.response?.data?.message || 'Ошибка смены пароля');
        return false;
      }
    },
    async uploadAvatar(file) {
      const formData = new FormData();
      formData.append('avatar', file);
      try {
        const response = await axios.post(`${baseURL}/auth/avatar`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const updatedUser = { ...this.user, avatar: response.data.avatar };
        this.user = updatedUser;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        const { useAlertStore } = require('./alert.module');
        useAlertStore().success('Фото обновлено');
      } catch (error) {
        const { useAlertStore } = require('./alert.module');
        useAlertStore().error(error.response?.data?.message || 'Ошибка загрузки фото');
      }
    },
    forgotPassword({ email }) {
      this.status = { sending: true };
      axios.post(`${baseURL}/auth/forgot-password`, { email })
        .then(() => {
          const { useAlertStore } = require('./alert.module');
          useAlertStore().success('Инструкции по восстановлению пароля отправлены на ' + email);
        })
        .catch(error => {
          const { useAlertStore } = require('./alert.module');
          const message = error.response?.data?.message || 'Ошибка отправки письма';
          useAlertStore().error(message);
        })
        .finally(() => {
          this.status = {};
        });
    },
    async resetPassword({ token, password }) {
      try {
        await axios.post(`${baseURL}/auth/reset-password`, { token, password }, { withCredentials: true });
        const { useAlertStore } = require('./alert.module');
        useAlertStore().success('Пароль успешно изменён. Войдите в систему.');
        router.push('/login');
      } catch (error) {
        const { useAlertStore } = require('./alert.module');
        useAlertStore().error(error.response?.data?.message || 'Ошибка сброса пароля');
      }
    },
    // Профиль: { id, name, login, partners: [{ id, name, type, role }] };
    // null — не загрузился (ошибку показал apiStore)
    async loadProfile() {
      try {
        const { useApiStore } = require('./API.module');
        return await useApiStore().send({
          method: 'get',
          requestURL: '/users/me',
        }) || null;
      } catch {
        return null;
      }
    },
    async sendLinkRequest({ partnerId, comment }) {
      const { useApiStore } = require('./API.module');
      await useApiStore().send({
        method: 'post',
        requestURL: '/user/partner-request',
        requestData: { partnerId, comment },
      });
    },
    async logout() {
      try {
        await axios.post(`${baseURL}/auth/logout`, {}, { withCredentials: true });
      } catch (e) {
        // игнорируем ошибку — выходим в любом случае
      }
      localStorage.removeItem('user');
      this.status = {};
      this.user = null;
      const pinia = getActivePinia();
      if (pinia) {
        pinia._s.forEach(store => {
          if (store.$id !== 'authentication') store.$reset();
        });
      }
      router.push('/login').catch(e => e);
    }
  }
});
